import { Injectable, inject, signal } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  ParamMap,
  Params,
  Router,
  Scroll,
} from '@angular/router';
import {
  firstValueFrom,
  Observable,
  filter,
  of,
  race,
  combineLatest,
} from 'rxjs';
import {
  delay,
  switchMap,
  map,
  shareReplay,
  distinctUntilChanged,
  take,
  catchError,
  tap,
} from 'rxjs/operators';
import { FilterKey } from './filter.keys.type';
import { Store } from '@ngxs/store';

import { AuthState } from '@wilson/auth/core';
import { captureException } from '@sentry/browser';
import { UserFiltersSettingsState } from './state/user-filter-settings/user-filter-settings.state';
import {
  RemoveUserFilterPageIdQueryParamsFromCache,
  SaveUserFilterQueryParamsInCache,
} from './state/user-filter-settings/user-filter-settings.actions';
import { PageFilterIdentifier } from './constants/page-filter-identifiers';
import { UserId } from './interfaces/filters.interfaces';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  private _numberOfAppliedFilters = signal<number>(0);
  public numberOfAppliedFiltersSignal =
    this._numberOfAppliedFilters.asReadonly();

  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);
  protected readonly store = inject(Store);

  //eslint-disable-next-line
  private async waitForNavigationToComplete(): Promise<any> {
    const NO_EVENT = 'NO_EVENT';
    const timeoutObservable = of(NO_EVENT).pipe(delay(100));
    const routerEventTracker = this.router.events.pipe(shareReplay(1));

    const combinedObservable = race(routerEventTracker, timeoutObservable);

    return firstValueFrom(
      combinedObservable.pipe(
        switchMap((event) => {
          if (event === NO_EVENT) {
            return of(null); // we don't wait anymore and resolve the promise
          } else {
            return routerEventTracker.pipe(
              filter(
                (event: any) =>
                  event instanceof NavigationEnd || event instanceof Scroll,
              ),
            );
          }
        }),
      ),
    );
  }

  public async saveValueInQueryParam(
    field: string,
    value: string,
  ): Promise<boolean> {
    await this.waitForNavigationToComplete();
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [field]: value ? value : null },
      queryParamsHandling: 'merge',
    });
  }

  public async saveValuesInQueryParam(fields: Params): Promise<boolean> {
    await this.waitForNavigationToComplete();
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: fields,
      queryParamsHandling: 'merge',
    });
  }

  public updateFiltersByPage(
    filterKeys: FilterKey[],
    pageId: PageFilterIdentifier,
  ): Observable<{
    userId: UserId;
    params: Params;
    savedFilterParams: unknown;
    count: number;
  }> {
    const userId$ = this.store.select(AuthState.userId);
    const queryParams$ = this.route.queryParams.pipe(
      distinctUntilChanged(
        (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
      ),
    );

    return combineLatest([userId$, queryParams$]).pipe(
      filter(([userId]) => !!userId),
      switchMap(([userId, params]) => {
        return this.handleFilterParamsByPage(
          userId as string,
          params,
          pageId,
        ).pipe(
          map((result) => ({
            ...result,
            count: filterKeys.filter((key) => params[key] !== undefined).length,
          })),
          catchError((error) => {
            console.error('Error updating filters:', error);
            captureException(error);
            // Return a safe default value to keep the stream alive
            return of({
              userId: userId as string,
              params,
              savedFilterParams: null,
              count: 0,
            });
          }),
        );
      }),
      tap(({ userId, params, savedFilterParams, count }) => {
        this._numberOfAppliedFilters.set(count);

        try {
          if (savedFilterParams) {
            this.saveValuesInQueryParam(savedFilterParams);
          } else {
            this.saveValuesInStorage(userId, params, pageId);
          }
        } catch (error) {
          console.error('Error saving filter values:', error);
          captureException(error);
        }
      }),
    );
  }

  public handleFilterParamsByPage(
    userId: UserId,
    params: Params,
    pageId: PageFilterIdentifier,
  ): Observable<{
    userId: UserId;
    params: Params;
    savedFilterParams: unknown;
  }> {
    const hasParams = Object.keys(params).length > 0;

    // if URL has no filter params, get saved params from storage, and use them to load data into the table
    if (!hasParams) {
      return this.store
        .select(UserFiltersSettingsState.getUserFilterValues(userId, pageId))
        .pipe(
          take(1),
          map((savedFilterParams) => ({
            userId,
            params,
            savedFilterParams,
          })),
        );
    }

    // if URL has filter params, proceed normally, and use them to load data into the table
    return of({
      userId,
      params,
      savedFilterParams: null,
    });
  }

  private saveValuesInStorage(
    userId: string,
    params: Params,
    pageId: PageFilterIdentifier,
  ): void {
    this.store.dispatch(
      new SaveUserFilterQueryParamsInCache(userId, pageId, params),
    );
  }

  private resetAllQueryParamsAndClearValuesInStorage(
    userId: string,
    pageId: PageFilterIdentifier,
  ): void {
    this.store.dispatch(
      new RemoveUserFilterPageIdQueryParamsFromCache(userId, pageId),
    );
    this.resetAllQueryParams();
  }

  public getValueFromQueryParam(field: string): Observable<string | null> {
    return this.route.queryParamMap.pipe(
      map((params: ParamMap) => params.get(field)),
      distinctUntilChanged(),
    );
  }

  public get isFiltered(): Observable<boolean> {
    return this.route.queryParams.pipe(
      map(
        (params: Params) =>
          !(
            params &&
            Object.keys(params).length === 0 &&
            Object.getPrototypeOf(params) === Object.prototype
          ),
      ),
    );
  }

  /** Reset all query parameters in the URL. If page support persistant filter storage then clear
   * stored filter values for the specified page.
   * @param pageId - (Optional) The identifier for the page whose filter values should be cleared from storage
   */
  public async resetAllQueryParams(
    pageId?: PageFilterIdentifier,
  ): Promise<boolean> {
    if (pageId) {
      const userId = await firstValueFrom(this.store.select(AuthState.userId));
      this.resetAllQueryParamsAndClearValuesInStorage(userId as string, pageId);
    }
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
  }

  public async setFiltersInQueryParams(
    filterCriteria: Record<FilterKey, string>,
  ): Promise<boolean> {
    await this.waitForNavigationToComplete();
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: filterCriteria,
    });
  }
}
